import React from 'react';
import {
    Paper,
    Typography,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from '@material-ui/core';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';

const toNumberOrNull = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};

const normalize = (text) => (typeof text === 'string' ? text.trim().toLowerCase() : '');
const stripDiacritics = (text) =>
    typeof text === 'string' ? text.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : '';

const getQuestionOption = (question, response) => {
    if (!question) return null;
    const refId = Number(response?.scaleOptionId);
    return (question.questionOptionScores || []).find((opt) => {
        const optId = opt.scaleOptionId || opt.scaleOption?.id || opt.id;
        return Number(optId) === refId;
    });
};

const deriveBinaryScore = (response, question) => {
    const direct = toNumberOrNull(response?.score);
    if (direct != null) return direct;

    const numericValue = toNumberOrNull(response?.numericValue);
    if (numericValue != null) return numericValue;

    let label = response?.textValue;
    const option = getQuestionOption(question, response);
    if (option) {
        const optScore = toNumberOrNull(option?.score);
        if (optScore != null) return optScore;
        label = label || option?.scaleOption?.label || option?.label || option?.value;
    }

    const normalized = normalize(label);
    if (!normalized) return null;
    const plain = stripDiacritics(normalized);
    if (plain === 'si' || plain === 'sí') return 1;
    if (plain === 'no') return 0;
    if (plain === '1' || plain === '0') return Number(plain);
    const numericFromText = Number(plain);
    if (!Number.isNaN(numericFromText)) return numericFromText;
    return null;
};

const computeRudResult = (register, scale) => {
    if (!register || !scale) return null;
    const responses = Array.isArray(register?.scaleResponses) ? register.scaleResponses : [];
    const questions = Array.isArray(scale?.questions) ? scale.questions : [];
    if (!responses.length || !questions.length) return null;

    const questionById = new Map(questions.map((q) => [String(q.id), q]));
    let totalScore = 0;

    responses.forEach((response) => {
        const question = questionById.get(String(response?.scaleQuestionId));
        const score = deriveBinaryScore(response, question);
        if (score != null) totalScore += score;
    });

    const totalMaxScore = questions.reduce((sum, q) => {
        const options = q.questionOptionScores || [];
        if (options.length) {
            const max = Math.max(...options.map((opt) => toNumberOrNull(opt?.score) ?? 0), 0);
            return sum + max;
        }
        return sum + 1;
    }, 0) || 15;

    const riskLevel = totalScore >= 6 ? 'Alto riesgo' : 'Bajo riesgo';

    return { totalScore, totalMaxScore, riskLevel };
};

export const ScaleResultRUD = ({
    title = 'Resultados',
    totalScore,
    totalMaxScore = 15,
    riskLevel,
    cutOffMessage = 'Puntuación de corte: 6 o mas indica alto riesgo.',
    onComputedResult,
    register,
    scale,
}) => {
    const derived = React.useMemo(() => computeRudResult(register, scale), [register, scale]);
    React.useEffect(() => {
            // Usamos onComputedResult, que es lo que recibimos por props
            if (derived && typeof onComputedResult === 'function') {
                onComputedResult(derived); 
            }
        }, [derived, onComputedResult]);
    const displayTotalScore = toNumberOrNull(totalScore) ?? derived?.totalScore ?? 0;
    const displayTotalMax = toNumberOrNull(totalMaxScore) ?? derived?.totalMaxScore ?? 15;
    const displayRiskLevel =
        riskLevel || derived?.riskLevel || (displayTotalScore >= 6 ? 'Alto riesgo' : 'Bajo riesgo');

    return (
        <Paper elevation={0} style={{ padding: 24, borderRadius: 12 }}>
            <Typography variant="h5" style={{ fontWeight: 700, marginBottom: 12, color: '#0d1b2a' }}>
                {title}
            </Typography>

            <Typography variant="subtitle1" style={{ fontWeight: 600, color: '#0d1b2a' }}>
                Puntuación Total: <span style={{ fontWeight: 400 }}>{displayTotalScore}/{displayTotalMax}</span>
            </Typography>

            {displayRiskLevel && (
                <Typography variant="subtitle1" style={{ fontWeight: 600, color: '#0d1b2a', marginTop: 12 }}>
                    Nivel de Riesgo: <span style={{ fontWeight: 400 }}>{displayRiskLevel}</span>
                </Typography>
            )}

            {cutOffMessage && (
                <List style={{ paddingTop: 16 }}>
                    <ListItem disableGutters>
                        <ListItemIcon style={{ minWidth: 24 }}>
                            <FiberManualRecordIcon fontSize="small" style={{ color: '#0d1b2a' }} />
                        </ListItemIcon>
                        <ListItemText
                            primaryTypographyProps={{ style: { color: '#0d1b2a' } }}
                            primary={cutOffMessage}
                        />
                    </ListItem>
                </List>
            )}
        </Paper>
    );
};

export default ScaleResultRUD;
